<template>
    <div class="inventory-list">
        <div v-if="!inventories.length" class="empty-state">
            <i class="fas fa-inbox"></i>
            <p>생성된 인벤토리가 없습니다.</p>
        </div>

        <div v-else class="inventory-table-container">
            <div class="table-header">
                <div class="header-left">
                    <h3 class="table-title">인벤토리 목록</h3>
                    <span class="count-badge">{{ inventories.length }}</span>
                </div>
                <div class="header-actions">
                    <CustomDropdown :items="headerDropdownItems" align="bottom" :append-to-body="true">
                        <template #trigger>
                            <button type="button" class="btn-dropdown-trigger" :disabled="disabled" title="작업 메뉴">
                                <i class="fas fa-ellipsis-v"></i>
                            </button>
                        </template>
                    </CustomDropdown>
                </div>
            </div>

            <div class="table-scroll-wrap">
                <common-table width="100%" margin="0" no-border>
                    <template #header-cells>
                        <th class="col-checkbox">
                            <input
                                type="checkbox"
                                class="checkbox-all"
                                :checked="isAllChecked"
                                :indeterminate.prop="isIndeterminate"
                                :disabled="disabled"
                                title="전체 선택"
                                @change="toggleSelectAll"
                            />
                        </th>
                        <th class="col-org-boundary">조직경계명</th>
                        <th class="col-period">기간</th>
                        <th class="col-emission">총배출량</th>
                        <th class="col-scope">Scope 1</th>
                        <th class="col-scope">Scope 2</th>
                        <th class="col-scope">Scope 3</th>
                    </template>
                    <template #body>
                        <tr
                            v-for="inventory in inventories"
                            :key="inventory.inventoryIdx"
                            class="clickable-row"
                            :class="{ 'is-active': selectedIdx === inventory.inventoryIdx }"
                            @click="$emit('select', inventory.inventoryIdx)"
                        >
                            <td class="col-checkbox" @click.stop>
                                <input
                                    type="checkbox"
                                    class="checkbox-row"
                                    :checked="selectedIdxs.includes(inventory.inventoryIdx)"
                                    :disabled="disabled"
                                    @change="toggleSelect(inventory.inventoryIdx)"
                                />
                            </td>
                            <td class="col-org-boundary">
                                <span class="org-boundary-name">{{
                                    getOrgBoundaryName(inventory.orgBoundaryIdx) || inventory.orgBoundaryName || "—"
                                }}</span>
                            </td>
                            <td class="col-period">
                                <div class="period-display">
                                    <span class="period-value">{{
                                        formatPeriod(inventory.period ?? inventory.periodFrom)
                                    }}</span>
                                    <template
                                        v-if="
                                            inventory.periodTo &&
                                            inventory.periodTo !== (inventory.period ?? inventory.periodFrom)
                                        "
                                    >
                                        <span class="period-separator">~</span>
                                        <span class="period-value">{{ formatPeriod(inventory.periodTo) }}</span>
                                    </template>
                                </div>
                            </td>
                            <td class="col-emission">
                                <span class="emission-total">{{ formatEmission(inventory.totalEmissions) }}</span>
                            </td>
                            <td class="col-scope" :class="{ 'is-zero': inventory.scope1Emissions === 0 }">
                                <span class="emission-value scope1">{{
                                    formatEmission(inventory.scope1Emissions)
                                }}</span>
                            </td>
                            <td class="col-scope" :class="{ 'is-zero': inventory.scope2Emissions === 0 }">
                                <span class="emission-value scope2">{{
                                    formatEmission(inventory.scope2Emissions)
                                }}</span>
                            </td>
                            <td class="col-scope" :class="{ 'is-zero': inventory.scope3Emissions === 0 }">
                                <span class="emission-value scope3">{{
                                    formatEmission(inventory.scope3Emissions)
                                }}</span>
                            </td>
                        </tr>
                    </template>
                </common-table>
            </div>
        </div>

        <!-- 인벤토리 비교 결과 모달 -->
        <InventoryCompareModal
            :visible="compareModalVisible"
            :loading="compareLoading"
            :result="compareResult"
            @close="handleCompareModalClose"
        />
    </div>
</template>

<script>
import CommonTable from "../../common/CommonTable.vue";
import { CustomDropdown } from "../../common";
import InventoryCompareModal from "../modals/InventoryCompareModal.vue";
import { fetchOrgBoundaryDetail } from "../../../services/boundaryService.js";
import carbonApi from "../../../api";
import { unwrapResponse, extractErrorMessage } from "../../../utils/api.js";

export default {
    name: "InventoryListTable",
    components: {
        CommonTable,
        CustomDropdown,
        InventoryCompareModal,
    },
    props: {
        inventories: {
            type: Array,
            default: () => [],
        },
        selectedIdx: {
            type: Number,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedIdxs: [],
            compareModalVisible: false,
            compareResult: null,
            compareLoading: false,
            /** orgBoundaryIdx -> orgBoundaryName (목록용 조회 캐시) */
            orgBoundaryNameMap: {},
        };
    },
    computed: {
        headerDropdownItems() {
            return [
                {
                    icon: "fa-balance-scale",
                    label: "인벤토리 비교",
                    disabled: this.selectedIdxs.length !== 2,
                    onClick: () => this.openCompareModal(),
                },
                {
                    icon: "fa-trash-alt",
                    label: "선택 삭제",
                    variant: "danger",
                    disabled: this.selectedIdxs.length === 0,
                    onClick: () => this.handleDeleteSelected(),
                },
            ];
        },
        isAllChecked() {
            if (!this.inventories.length) return false;
            // 숫자로 변환하여 비교 (타입 일치 보장)
            return this.inventories.every((inv) => this.selectedIdxs.includes(Number(inv.inventoryIdx)));
        },
        isIndeterminate() {
            const n = this.selectedIdxs.length;
            return n > 0 && n < this.inventories.length;
        },
    },
    watch: {
        inventories(list) {
            // 숫자로 변환하여 비교 (타입 일치 보장)
            this.selectedIdxs = this.selectedIdxs.filter((id) =>
                list.some((inv) => Number(inv.inventoryIdx) === Number(id)),
            );
            this.loadOrgBoundaryNames();
        },
    },
    mounted() {
        this.loadOrgBoundaryNames();
    },
    methods: {
        /** 목록에 있는 orgBoundaryIdx별로 조직경계명 조회 (DetailView와 동일 API) */
        async loadOrgBoundaryNames() {
            if (!this.inventories?.length) return;
            const ids = [...new Set(this.inventories.map((inv) => inv.orgBoundaryIdx).filter(Boolean))];
            const toFetch = ids.filter((id) => !this.orgBoundaryNameMap[String(id)]);
            if (toFetch.length === 0) return;
            const results = await Promise.allSettled(
                toFetch.map((id) => fetchOrgBoundaryDetail(id).then((r) => ({ id, data: r }))),
            );
            const next = { ...this.orgBoundaryNameMap };
            results.forEach((res) => {
                if (res.status === "fulfilled" && res.value?.id != null) {
                    const data = res.value.data;
                    const name = data?.orgBoundaryName ?? data?.data?.orgBoundaryName ?? null;
                    if (name != null) next[String(res.value.id)] = name;
                }
            });
            this.orgBoundaryNameMap = next;
        },
        getOrgBoundaryName(orgBoundaryIdx) {
            if (orgBoundaryIdx == null) return null;
            return this.orgBoundaryNameMap[String(orgBoundaryIdx)] ?? null;
        },
        toggleSelectAll() {
            if (this.isAllChecked) {
                this.selectedIdxs = [];
            } else {
                // inventoryIdx를 숫자로 변환하여 저장
                this.selectedIdxs = this.inventories
                    .map((inv) => {
                        const idx = Number(inv.inventoryIdx);
                        return isNaN(idx) ? null : idx;
                    })
                    .filter((id) => id !== null);
            }
        },
        toggleSelect(inventoryIdx) {
            // 숫자로 변환하여 비교 및 저장
            const numIdx = Number(inventoryIdx);
            if (isNaN(numIdx)) return;

            const idx = this.selectedIdxs.findIndex((id) => Number(id) === numIdx);
            if (idx === -1) {
                this.selectedIdxs = [...this.selectedIdxs, numIdx];
            } else {
                this.selectedIdxs = this.selectedIdxs.filter((id) => Number(id) !== numIdx);
            }
        },
        handleDeleteSelected() {
            if (this.selectedIdxs.length === 0) return;
            this.$emit("delete-selected", [...this.selectedIdxs]);
        },
        formatPeriod(period) {
            if (!period || String(period).length !== 6) return period;
            return `${String(period).slice(0, 4)}.${String(period).slice(4, 6)}`;
        },
        formatEmission(value) {
            if (value == null) return "-";

            const num = Number(value);
            if (num === 0) return "0";

            const absNum = Math.abs(num);

            // 1,000,000 이상 → M (백만)
            if (absNum >= 1000000) {
                const formatted = (num / 1000000).toFixed(1);
                return formatted.endsWith(".0") ? Math.round(num / 1000000) + "M" : formatted + "M";
            }

            // 10,000 이상 → K (만)
            if (absNum >= 10000) {
                const formatted = (num / 1000).toFixed(0);
                return formatted + "K";
            }

            // 1,000 이상 → K (천)
            if (absNum >= 1000) {
                const formatted = (num / 1000).toFixed(1);
                return formatted.endsWith(".0") ? Math.round(num / 1000) + "K" : formatted + "K";
            }

            // 1,000 미만 → 소수점 1자리
            return num.toFixed(1);
        },
        openCompareModal() {
            if (this.selectedIdxs.length !== 2) return;
            this.compareModalVisible = true;
            this.compareResult = null;
            this.fetchCompare();
        },
        async fetchCompare() {
            if (this.selectedIdxs.length !== 2) return;

            // selectedIdxs에서 숫자 값만 추출 (배열 중첩 방지)
            const getNumericId = (val) => {
                if (Array.isArray(val)) {
                    const first = val[0];
                    return Array.isArray(first) ? Number(first[0]) : Number(first);
                }
                return Number(val);
            };

            const id1 = getNumericId(this.selectedIdxs[0]);
            const id2 = getNumericId(this.selectedIdxs[1]);

            // 유효성 검증
            if (isNaN(id1) || isNaN(id2) || id1 <= 0 || id2 <= 0) {
                this.$bvToast?.toast("유효하지 않은 인벤토리 ID입니다.", {
                    variant: "danger",
                });
                this.compareModalVisible = false;
                return;
            }

            this.compareLoading = true;
            try {
                // 명시적으로 숫자로 전달 (배열이 아닌 단일 숫자 값)
                const res = await carbonApi.inventory.compareInventories(Number(id1), Number(id2));
                const data = unwrapResponse(res, { defaultMessage: "인벤토리 비교 조회에 실패했습니다." });
                this.compareResult = data?.data ?? data ?? null;
            } catch (e) {
                this.$bvToast?.toast(extractErrorMessage(e, "인벤토리 비교 조회에 실패했습니다."), {
                    variant: "danger",
                });
                this.compareModalVisible = false;
            } finally {
                this.compareLoading = false;
            }
        },
        handleCompareModalClose() {
            this.compareModalVisible = false;
            this.compareResult = null;
        },
    },
};
</script>

<style scoped>
.inventory-list {
    display: flex;
    flex-direction: column;
    height: 100%;
    font-size: 12px;
}

/* Empty 상태 */
.empty-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: #999999;
    text-align: center;
}

.empty-state i {
    color: #cccccc;
    margin-bottom: 12px;
}

.empty-state p {
    margin: 0;
}

/* 테이블 컨테이너 */
.inventory-table-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
}

.table-scroll-wrap {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}

.table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    border-bottom: 1px solid #e0e0e0;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 6px;
}

.header-actions {
    display: flex;
    align-items: center;
}

.btn-dropdown-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    color: #999999;
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 15px;
    transition:
        color 0.2s ease,
        transform 0.15s ease;
}

.btn-dropdown-trigger:hover:not(:disabled) {
    color: #40916c;
    transform: scale(1.05);
}

.btn-dropdown-trigger:active:not(:disabled) {
    transform: scale(0.98);
}

.btn-dropdown-trigger:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.table-title {
    margin: 0;
    font-weight: 700;
    color: #1a1a1a;
    font-size: 14px;
}

.count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 16px;
    padding: 0 4px;
    font-weight: 600;
    color: #666666;
    background: #f5f5f5;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
}

/* 체크박스 컬럼 */
.col-checkbox {
    width: 40px;
    text-align: center;
    vertical-align: middle;
}

.col-checkbox input[type="checkbox"] {
    cursor: pointer;
    width: 16px;
    height: 16px;
    accent-color: #52b788;
}

.col-checkbox .checkbox-all {
    cursor: pointer;
}

/* 조직경계명 · 기간 · Scope 배출량: 셀 폰트 통일 */
.col-org-boundary .org-boundary-name,
.period-value,
.emission-value {
    font-size: 12px;
    font-weight: 700;
    color: #333333;
}

.col-org-boundary {
    min-width: 80px;
    max-width: 10em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.org-boundary-name {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* 기간 컬럼 */
.period-display {
    display: flex;
    align-items: center;
    gap: 2px;
}

.period-separator {
    color: #999999;
    font-weight: 500;
}

/* 총배출량 컬럼 (강조 유지) */
.col-emission {
    text-align: right;
}

.emission-total {
    display: inline-block;
    font-size: 11px;
    font-weight: 600;
    color: #ffffff;
    padding: 2px 8px;
    background: #52b788;
    border-radius: 3px;
}

/* Scope 컬럼 */
.col-scope {
    text-align: right;
}

.emission-value {
    display: inline-block;
    padding: 2px 4px;
    border-radius: 3px;
}

.is-zero .emission-value {
    color: #999999;
    font-weight: 400;
}

/* 반응형 */
@media (max-width: 1024px) {
    .emission-value {
        padding: 2px 4px;
    }
}
</style>
